import {
  ComponentProps,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import StyledReactModal from "styled-react-modal";
import { createFocusTrap, FocusTrap } from "focus-trap";
import _uniqueId from "lodash/uniqueId";
import { ModalContextProvider } from "./ModalContext";
import { Title, Content, Actions, CloseButton } from "./components";
import "../../designTokens/variables.css";
import styled from "styled-components";
import { clsx } from "clsx";
import { alpha } from "../../typography";
import { AllIconName, IconType, PlainIcon } from "../Icon";
import { StyledModal } from "./StyledModalComponents";

const Steps = styled.ul`
  display: flex;
  justify-content: center;
  gap: 12px;
  list-style-type: none;
  margin: 0;
  ${alpha.primaryActive.body2}
  &.padding-without-close-btn {
    padding-top: 24px;
    padding-bottom: 0px !important;
  }
`;

const Step = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: var(--color-primary-400);

  &.active {
    border: 1px solid var(--color-primary-800);
    color: var(--color-primary-800);
  }
`;

const CloseIconContainer = styled.span`
  position: absolute;
  top: 24px;
  right: 0px;
  cursor: pointer;
  > span {
    display: block;
  }

  &.hide-btn {
    display: none;
  }
`;

const Subtitle = styled.div`
  ${alpha.primaryActive.body3};
`;

type BaseModalProps = ComponentProps<typeof StyledReactModal>;

export enum ModalTestIds {
  Container = "modal",
}

type ModalProps = {
  afterClose?: BaseModalProps["afterClose"];
  afterOpen?: BaseModalProps["afterOpen"];
  beforeClose?: BaseModalProps["beforeClose"];
  beforeOpen?: BaseModalProps["beforeOpen"];
  isOpen: boolean;
  disableBackgroundClick?: boolean;
  onClose: () => void;
  children?: ReactNode;
  id?: string;
  testId?: string;
  closeIconId?: string;
  className?: string;
  title: {
    text: string;
    iconName?: AllIconName;
    iconType?: IconType;
    bgColor?: string;
    id?: string;
  };
  subtitle?: string | ReactElement;
  size?: "small" | "medium" | "large" | "extra-large" | "fullscreen";
  totalSteps?: number;
  currentStep?: number;
  alignment?: "top" | "center" | "bottom";
  hasCloseButton?: boolean;
};

export const Modal = ({
  isOpen,
  onClose,
  children,
  className = "",
  testId,
  id,
  closeIconId,
  disableBackgroundClick,
  title,
  subtitle,
  size = "large",
  totalSteps,
  currentStep,
  hasCloseButton = true,
  afterOpen,
  afterClose,
  ...modalProps
}: ModalProps) => {
  const modalSize = clsx({
    ["small"]: size === "small",
    ["medium"]: size === "medium",
    ["large"]: size === "large",
    ["extra-large"]: size === "extra-large",
    ["fullscreen"]: size === "fullscreen",
  });

  const [generatedId] = useState(_uniqueId("modal-"));
  const [generatedTitleId] = useState(_uniqueId("modal-title-"));
  const containerId = id ?? generatedId;
  const titleId = title.id ?? generatedTitleId;

  const focusTrapRef = useRef<FocusTrap | null>(null);

  // Kept in refs (rather than effect deps) so the handlers below can keep a stable identity
  // across re-renders: styled-react-modal re-invokes afterOpen/afterClose whenever their
  // reference changes, so a new function on every render would re-trigger the focus trap.
  const afterOpenRef = useRef(afterOpen);
  afterOpenRef.current = afterOpen;
  const afterCloseRef = useRef(afterClose);
  afterCloseRef.current = afterClose;

  const handleAfterOpen = useCallback(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    focusTrapRef.current = createFocusTrap(container, {
      fallbackFocus: container,
      escapeDeactivates: false,
      clickOutsideDeactivates: false,
      allowOutsideClick: true,
    });
    focusTrapRef.current.activate();
    afterOpenRef.current?.();
  }, [containerId]);

  const handleAfterClose = useCallback(() => {
    focusTrapRef.current?.deactivate();
    focusTrapRef.current = null;
    afterCloseRef.current?.();
  }, []);

  useEffect(() => {
    return () => {
      focusTrapRef.current?.deactivate();
      focusTrapRef.current = null;
    };
  }, []);

  return (
    <ModalContextProvider value={{ onClose }}>
      <StyledModal
        isOpen={isOpen}
        {...(!disableBackgroundClick && { onBackgroundClick: onClose })}
        onEscapeKeydown={onClose}
        afterOpen={handleAfterOpen}
        afterClose={handleAfterClose}
        // @ts-expect-error ts(2769) library styled-react-modal doesn't expect className but it still works
        className={`${className} ${modalSize}${
          !hasCloseButton ? " no-close-btn" : ""
        }`}
        data-testid={testId ?? ModalTestIds.Container}
        id={containerId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...modalProps}
      >
        {hasCloseButton && (
          <CloseIconContainer>
            <PlainIcon id={closeIconId} iconName="x-close" onClick={onClose} />
          </CloseIconContainer>
        )}
        {totalSteps && (
          <Steps className={hasCloseButton ? "" : "padding-without-close-btn"}>
            {[...Array(totalSteps)].map((_step, index) => {
              return (
                <Step
                  key={index}
                  className={index === currentStep ? "active" : ""}
                >
                  <span>{index + 1}</span>
                </Step>
              );
            })}
          </Steps>
        )}
        <Title
          titleId={titleId}
          iconName={title.iconName}
          iconType={title.iconType}
        >
          {title.text}
        </Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {children}
      </StyledModal>
    </ModalContextProvider>
  );
};

Modal.Content = Content;
Modal.Actions = Actions;
Modal.CloseButton = CloseButton;
